import { useMutation } from '@apollo/client/react';
import type { Reference } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    CreateCollectionDocument,
    ICreateLibraryCollectionInput,
} from 'graphql/types';
import { PageDrawer } from 'common/components/PageDrawer';
import { LibraryCollectionForm } from 'library/components/forms/LibraryCollectionForm';
import { ApolloError } from 'common/components/ApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { PageTitle } from 'common/components/PageTitle';
import { getImageId } from 'common/utils/image';
import { formatColor } from 'common/utils/color';

interface IProps {
    pageTitle?: string;
    open?: boolean;
    parentId: string;
    onCreate?(): void;
    onClose?(): void;
}

export const LibraryCollectionCreateDrawer = ({
    pageTitle,
    open,
    parentId,
    onCreate,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();

    const [createCollection, { error: createError }] = useMutation(
        CreateCollectionDocument,
        {
            update: (cache, { data }) => {
                const newCollection = data?.createLibraryCollection?.collection;

                if (!newCollection) return;

                // Mutation already wrote the collection; append its ref to the list.
                cache.modify({
                    id: `LibraryCollection:${parentId}`,
                    fields: {
                        collections(
                            existingRefs = [],
                            { readField, toReference }
                        ) {
                            const newRef = toReference(newCollection);

                            if (!newRef) {
                                return existingRefs;
                            }

                            if (
                                existingRefs.some(
                                    (ref: Reference) =>
                                        readField('id', ref) ===
                                        newCollection.id
                                )
                            ) {
                                return existingRefs;
                            }

                            return [...existingRefs, newRef];
                        },
                    },
                });
            },
        }
    );

    const initialValues = {
        title: '',
        description: '',
        active: false,
        titleColor: '',
        accessTrainings: [],
        accessGroups: [],
    };

    return (
        <PageDrawer open={open} onClose={onClose}>
            {createError && <ApolloError error={createError} />}

            {pageTitle && (
                <PageTitle>{`${pageTitle} - ${translate(
                    'newCollection'
                )}`}</PageTitle>
            )}

            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 'bold', mt: 0 }} variant="h3">
                        {translate('createCollection')}
                    </Typography>
                </Box>

                <LibraryCollectionForm
                    initialValues={initialValues}
                    onClose={onClose}
                    onSubmit={async (values) => {
                        const {
                            accessGroups,
                            accessTrainings,
                            thumbnail,
                            headerImage,
                            titleColor,
                            ...collectionInput
                        } = values;

                        const access = [
                            ...accessTrainings.map((training) => training.id),
                            ...accessGroups.map((group) => group.id),
                        ];

                        const newCollection = {
                            ...collectionInput,
                            titleColor: formatColor(titleColor),
                            thumbnailId: getImageId(undefined, thumbnail),
                            headerImageId: getImageId(undefined, headerImage),
                            access,
                            parentId,
                        } as ICreateLibraryCollectionInput;

                        const result = await createCollection({
                            variables: {
                                collection: newCollection,
                            },
                        });

                        if (!result.data?.createLibraryCollection?.collection) {
                            return;
                        }

                        displaySnackbar(
                            translate('libraryCollectionActionSuccess.create'),
                            {
                                variant: 'success',
                            }
                        );

                        onCreate?.();
                    }}
                />
            </Box>
        </PageDrawer>
    );
};
